import type {
	Action,
	ActionThunk,
	BaseContainerProps,
	GenericContainerComponent,
	Store,
} from '../types.tsx';
import hash from '../utils/hash.tsx';

function createKey<TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
	initialState: TState,
	actions: TActions,
	name: string,
) {
	const src = !name ? Object.keys(actions).reduce((acc, k) => acc + actions[k].toString(), '') : '';
	return [name, hash(src + JSON.stringify(initialState))].filter(Boolean).join('__');
}

export type StateInitializer<TState extends object> = TState | (() => TState);

export function createStore<
	TState extends object,
	TActions extends Record<string, ActionThunk<TState, TActions>>,
	TContainerProps = unknown,
>(
	config:
		| {
				initialState: StateInitializer<TState>;
				actions: TActions;
				name?: string;
				containedBy?: never;
				handlers?: never;
		  }
		| {
				initialState: StateInitializer<TState>;
				actions: TActions;
				name?: string;
				containedBy: GenericContainerComponent<TContainerProps>;
				handlers?: {
					/**
					 * @deprecated Do not use this. If you must, ensure that the associated container has getIsConcurrentSafe resolve to `true`. See https://hello.atlassian.net/wiki/spaces/JDP/pages/4802027965/ADR+react-sweet-state+RSS+concurrent+mode+compatibility
					 */
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					onInit?: () => Action<TState, TContainerProps, any>;
					/**
					 * @deprecated Do not use this. If you must, ensure that the associated container has getIsConcurrentSafe resolve to `true`. See https://hello.atlassian.net/wiki/spaces/JDP/pages/4802027965/ADR+react-sweet-state+RSS+concurrent+mode+compatibility
					 */
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					onUpdate?: () => Action<TState, TContainerProps, any>;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					onDestroy?: () => Action<TState, TContainerProps, any>;
					/**
					 * @deprecated Do not use this. If you must, ensure that the associated container has getIsConcurrentSafe resolve to `true`. See https://hello.atlassian.net/wiki/spaces/JDP/pages/4802027965/ADR+react-sweet-state+RSS+concurrent+mode+compatibility
					 */
					onContainerUpdate?: (
						nextProps: BaseContainerProps & TContainerProps,
						prevProps: BaseContainerProps & TContainerProps,
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					) => Action<TState, TContainerProps, any>;
				};
		  },
): Store<TState, TActions>;

export function createStore({
	name = '',
	initialState,
	actions,
	containedBy,
	handlers = {},
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
}: any): any {
	let key: string;
	return {
		get key() {
			// lazy evaluate key on first access
			// eslint-disable-next-line no-return-assign
			return key || (key = createKey(initialState, actions, name));
		},
		initialState,
		actions,
		containedBy,
		handlers,
	};
}
